import React, { useEffect, useState, useRef } from 'react';
import { Input, InputAdornment } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import Narrow from '@sg/assests/images/Narrow.svg';
import AutoTextarea from '@gp/components/AutoTextareaNew';
import './index.scss';

// 定义不同的渲染样式
type RenderStyle = 'Opportunities & Risks' | 'Action & Note' | 'default';

interface EditableFieldProps {
    initialData: any;
    renderStyle?: RenderStyle;
    onChange: (TableData: any) => void;
}

const getFieldValue = (data: any, style: RenderStyle): string => {
    if (style === 'Opportunities & Risks') {
        return data?.['Opportunities & Risks'] || '';
    }
    if (style === 'Action & Note') {
        return data?.['Action & Note'] || '';
    }
    return '';
};

const EditableField: React.FC<EditableFieldProps> = ({
    initialData,
    renderStyle = 'default',
    onChange,
}) => {
    const [tableData, setTableData] = useState(initialData);
    // 用来标记组件是否是第一次加载
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    // Seed from initialData on first paint to avoid empty-input flash before useEffect runs.
    const [localValue, setLocalValue] = useState<string>(() => getFieldValue(initialData, renderStyle));
    // Debounce timer ref
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    
    useEffect(() => {
        // 组件第一次加载时不触发 onChange
        if (isFirstLoad) {
            setIsFirstLoad(false);
        }
    }, [isFirstLoad]);

    // Keep local value in sync when the row data changes (e.g. auto-save restore).
    useEffect(() => {
        setLocalValue(getFieldValue(initialData, renderStyle));
    }, [initialData, renderStyle]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const handleFieldChange = (field: string, value: any) => {
        // 更新本地状态（立即更新输入框显示）
        setLocalValue(value);
        setTableData((prevData: any) => {
            return { ...prevData, [field]: value };
        });

        // 清除之前的定时器
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // 设置新的定时器：3秒后触发onChange（用户停止输入3秒后才保存）
        debounceTimerRef.current = setTimeout(() => {
            if (!isFirstLoad) {
                onChange(value);
            }
        }, 3000);
    };

    const handleFieldBlur = (field: string) => {
        // 失去焦点时立即触发onChange（清除定时器并立即保存）
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        
        if (!isFirstLoad) {
            onChange(localValue);
        }
    };

    // 阻止鼠标事件冒泡
    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // 阻止事件冒泡
    };

    // 阻止鼠标按下事件冒泡
    const handleMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation(); // 阻止事件冒泡
    };

    // 阻止焦点事件冒泡
    const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        e.stopPropagation(); // 阻止事件冒泡
    };
    
    // 不同样式的渲染
    const renderInputField = () => {
        switch (renderStyle) {
            case 'Opportunities & Risks':
                const isPlaceholderActive = !localValue || localValue.trim() === '';
                return (<div onClick={handleClick}>
                    {/* {tableData?.['Opportunities & Risks'] ? <InputAdornment style={{ backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center' }} before={<span style={{ border: '1px solid #d8dae4', borderRadius: '4px 0 0 4px', padding: '10PX 12px', fontSize: '12px' }}>
                        {((tableData['25F'] - tableData['25B']) > 0 ?
                            <SvgIcon style={{ color: '#69D19C', position: 'relative', top: '-1px' }} type={'cross'} /> :
                            (tableData['25F'] - tableData['25B']) === 0 ? <span>-</span> : <img width="12px" src={Narrow} alt="" style={{ position: 'relative', top: '1px' }} />)}
                    </span>}>
                        <Input onFocus={handleFocus} onMouseDown={handleMouseDown} value={tableData?.['Opportunities & Risks']} onChange={(value) => handleFieldChange('Opportunities & Risks', value)} />
                    </InputAdornment> : <span>-</span>} */}
                    <InputAdornment 
                        style={{ 
                            backgroundColor: '#f0f0f0', 
                            display: 'flex', 
                        }} 
                        before={
                            <span style={{ 
                                boxSizing: 'border-box',
                                border: '1px solid #d8dae4', 
                                borderRadius: '4px 0 0 4px', 
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '36px',
                                minHeight: '36px',
                                height: '100%',
                                backgroundColor: '#F5F5F8',
                                lineHeight: 0,
                                padding: '8px 0'
                            }}>
                                {((tableData['25F'] - tableData['25B']) > 0 ?
                                    <SvgIcon style={{ color: '#69D19C' }} type={'cross'} /> :
                                    (tableData['25F'] - tableData['25B']) === 0 ? <span>-</span> : <img width="12px" src={Narrow} alt="" />)}
                            </span>
                        }
                    >
                        <AutoTextarea
                            className="dbs-editable-field-textarea opportunities-risks"
                            placeholder='Please enter' 
                            minHeight={36}
                            style={{ 
                                width: '100%', 
                            }} 
                            onFocus={handleFocus as any} 
                            onMouseDown={handleMouseDown as any} 
                            value={localValue} 
                            onChange={(value: string) => handleFieldChange('Opportunities & Risks', value)}
                            onBlur={() => handleFieldBlur('Opportunities & Risks')}
                        />
                    </InputAdornment>
                </div>);
            case 'Action & Note':
                return (
                    <>
                        <AutoTextarea
                            className="dbs-editable-field-textarea"
                            style={{ 
                                width: '100%'
                            }}
                            minHeight={36}
                            onFocus={handleFocus as any}
                            onMouseDown={handleMouseDown as any}
                            value={localValue} // 使用 localValue 来绑定值
                            onChange={(value: string) => handleFieldChange('Action & Note', value)} // 处理 onChange 事件
                            onBlur={() => handleFieldBlur('Action & Note')}
                            placeholder='Please enter'
                        />
                    </>
                );
            default:
                return (
                    <div> - </div>
                );
        }
    };

    return <div onClick={handleClick} onMouseDown={handleMouseDown}>{renderInputField()}</div>;
};

export default EditableField;
